import { writable, derived, type Writable } from 'svelte/store';
import type { 
  AISettings, 
  AIServiceState, 
  AIModelInfo, 
  TokenUsage, 
  UsageEntry,
  AIAnalysisRequest,
  AIAnalysisResult
} from '$lib/types/ai';
import { AIAnalysisService } from '$lib/services/ai/AIAnalysisService';
import { settingsStore, aiProvidersStore, defaultSettings } from './settings';

/**
 * AI Service Store for Svelte Application
 * Manages AI analysis service state and integrates with settings
 */

// Global AI service instance
let aiServiceInstance: AIAnalysisService | null = null;

// Core service state
export const aiServiceState = writable<AIServiceState>({
  isInitialized: false,
  isAnalyzing: false,
  currentModel: null,
  lastError: null,
  analysisResults: null,
  tokenUsage: null
});

// Usage tracking
export const usageHistory = writable<UsageEntry[]>([]);
export const dailyUsage = writable<TokenUsage>({ prompt: 0, completion: 0, total: 0, cost: 0 });

// Model selection and availability
export const availableModels = writable<AIModelInfo[]>([]);
export const selectedModel = writable<string>('');
export const selectedProvider = writable<string>('');

// Derived AI settings from main settings store
export const aiSettings = derived(
  [settingsStore, selectedModel, selectedProvider],
  ([$settings, $selectedModel, $selectedProvider]) => {
    // Handle case where settings are not yet loaded
    if (!$settings || !$settings.aiProviders) {
      return {
        provider: 'openrouter' as any,
        openaiApiKey: '',
        anthropicApiKey: '',
        googleApiKey: '',
        upstageApiKey: '',
        openrouterApiKey: '',
        cerebrasApiKey: '',
        groqApiKey: '',
        selectedModel: 'openai/gpt-oss-120b',
        contextWindow: 128000,
        showTokenWarning: true,
        tokenWarningThreshold: 100000,
        enableLogging: false,
        logLevel: 'info' as const,
        providerModels: [],
        maxTokens: 2000,
        temperature: 0.3,
        streamMode: false,
        maxRetries: 3,
        timeout: 30000
      };
    }
    
    // Find enabled AI provider (priority: enabled providers, then fallback to openrouter)
    const enabledProvider = $settings.aiProviders.find(p => p.enabled && (p.apiKey || p.id === 'openrouter'));
    const fallbackProvider = $settings.aiProviders.find(p => p.id === 'openrouter');
    const activeProvider = enabledProvider || fallbackProvider;
    
    // Use active provider or fallback to selected provider
    const targetProvider = activeProvider || $settings.aiProviders.find(p => p.id === $selectedProvider);
    
    const aiSettings: AISettings = {
      provider: (targetProvider?.id || $selectedProvider || 'openrouter') as any,
      openaiApiKey: targetProvider?.id === 'openai' ? targetProvider.apiKey : '',
      anthropicApiKey: targetProvider?.id === 'anthropic' ? targetProvider.apiKey : '',
      googleApiKey: targetProvider?.id === 'google' ? targetProvider.apiKey : '',
      upstageApiKey: targetProvider?.id === 'upstage' ? targetProvider.apiKey : '',
      openrouterApiKey: targetProvider?.id === 'openrouter' ? targetProvider.apiKey : '',
      cerebrasApiKey: targetProvider?.id === 'cerebras' ? targetProvider.apiKey : '',
      groqApiKey: targetProvider?.id === 'groq' ? targetProvider.apiKey : '',
      selectedModel: $selectedModel || targetProvider?.defaultModel || 'openai/gpt-oss-120b',
      contextWindow: 128000, // Default context window
      showTokenWarning: true,
      tokenWarningThreshold: 100000,
      maxTokens: 2000,
      temperature: 0.7, // Default temperature
      enabled: !!targetProvider?.enabled,
      isPremium: false, // This would come from subscription state
      isTrial: true     // This would come from trial state
    };
    
    return aiSettings;
  }
);

// AI service actions
export const aiServiceActions = {
  /**
   * Initialize the AI service with current settings
   */
  async initialize(): Promise<void> {
    try {
      const currentSettings = await new Promise<AISettings>(resolve => {
        let unsubscribe: (() => void) | null = null;
        unsubscribe = aiSettings.subscribe(settings => {
          resolve(settings as AISettings);
          if (unsubscribe) unsubscribe();
        });
      });

      if (!aiServiceInstance) {
        aiServiceInstance = new AIAnalysisService(currentSettings);
        
        // Subscribe to service state updates
        aiServiceInstance.state.subscribe(state => {
          aiServiceState.set(state);
        });
      } else {
        // Update existing service with new settings
        aiServiceInstance.updateSettings(currentSettings);
      }

      await aiServiceInstance.initializeClient();
    } catch (error) {
      console.error('Failed to initialize AI service:', error);
      aiServiceState.update(state => ({
        ...state,
        lastError: error instanceof Error ? error.message : 'Initialization failed'
      }));
    }
  },

  /**
   * Analyze corrections using AI
   */
  async analyzeCorrections(request: AIAnalysisRequest): Promise<AIAnalysisResult[]> {
    if (!aiServiceInstance) {
      throw new Error('AI service not initialized');
    }

    try {
      const results = await aiServiceInstance.analyzeCorrections(request);
      
      // Track usage
      this.trackUsage({
        timestamp: Date.now(),
        provider: await new Promise<string>(resolve => {
          const unsubscribe = selectedProvider.subscribe(provider => {
            resolve(provider);
            unsubscribe();
          });
        }),
        model: await new Promise<string>(resolve => {
          const unsubscribe = selectedModel.subscribe(model => {
            resolve(model);
            unsubscribe();
          });
        }),
        tokenUsage: { 
          prompt: aiServiceInstance!.estimateTokenUsage(request), 
          completion: 500, // Estimated
          total: aiServiceInstance!.estimateTokenUsage(request) + 500,
          cost: 0.01 // Estimated
        },
        success: true,
        responseTime: 1000 // This would be tracked in the actual service
      });

      return results;
    } catch (error) {
      console.error('AI analysis failed:', error);
      throw error;
    }
  },

  /**
   * Select AI model and update service
   */
  async selectModel(modelId: string, provider: string): Promise<void> {
    // OpenRouter doesn't need API key (proxy service)
    if (provider !== 'openrouter') {
      // Check if provider has API key configured
      const settings = settingsStore.get();
      const aiProvider = settings.aiProviders.find(p => p.id === provider);
      
      if (!aiProvider?.apiKey) {
        aiServiceState.update(state => ({
          ...state,
          lastError: `No API key configured for ${provider}. Please add it in settings.`
        }));
        return;
      }
    }
    
    selectedModel.set(modelId);
    selectedProvider.set(provider);
    
    // Save selected model to settings for persistence
    const current = settingsStore.get();
    const currentAi = current.ai || defaultSettings.ai;
    settingsStore.set({
      ...current,
      ai: {
        ...defaultSettings.ai,
        ...currentAi,
        selectedModel: modelId,
        provider: provider as any,
        groqApiKey: (currentAi as any).groqApiKey || '',
        cerebrasApiKey: (currentAi as any).cerebrasApiKey || ''
      } as AISettings
    });
    
    // Reinitialize service with new model
    await this.initialize();
  },

  /**
   * Check if AI service is available
   */
  async isAvailable(): Promise<boolean> {
    // First check if we have any configured API keys or OpenRouter (which doesn't need an API key)
    const settings = settingsStore.get();
    const hasConfiguredProvider = settings.aiProviders.some(
      p => p.enabled && (
        (p.apiKey && p.apiKey.trim() !== '') || 
        p.id === 'openrouter' // OpenRouter doesn't need an API key
      )
    );
    
    // Also check AI settings enabled flag
    const aiEnabled = settings.ai?.enabled ?? false;
    
    if (!hasConfiguredProvider || !aiEnabled) {
      return false;
    }
    
    if (!aiServiceInstance) {
      // Try to initialize if not already done
      try {
        await this.initialize();
      } catch (error) {
        console.error('Failed to initialize AI service:', error);
        return false;
      }
    }
    
    if (!aiServiceInstance) {
      return false;
    }
    
    return await aiServiceInstance.isAvailable();
  },

  /**
   * Get access status for AI features
   */
  getAccessStatus() {
    if (!aiServiceInstance) {
      return {
        allowed: false,
        reason: 'not_initialized' as const,
        statusMessage: 'AI service not initialized'
      };
    }
    return aiServiceInstance.getAccessStatus();
  },

  /**
   * Clear analysis results
   */
  clearResults(): void {
    if (aiServiceInstance) {
      aiServiceInstance.clearResults();
    }
  },

  /**
   * Cancel ongoing analysis and reset state
   */
  cancel(): void {
    if (aiServiceInstance) {
      aiServiceInstance.cancel();
    }

    // Get current state to preserve initialized status
    let currentState: any = null;
    const unsubscribe = aiServiceState.subscribe(state => {
      currentState = state;
    });
    unsubscribe();

    // Force reset the analyzing state while preserving initialized and currentModel
    aiServiceState.set({
      isInitialized: currentState?.isInitialized || false,
      isAnalyzing: false,
      currentModel: currentState?.currentModel || null,
      lastError: null,
      analysisResults: null,
      tokenUsage: null
    });
  },

  /**
   * Force reset analyzing state
   */
  forceResetAnalyzing(): void {
    aiServiceState.update(state => ({
      ...state,
      isAnalyzing: false
    }));
  },

  /**
   * Track usage for analytics
   */
  trackUsage(entry: UsageEntry): void {
    usageHistory.update(history => {
      const newHistory = [...history, entry];
      // Keep only last 1000 entries
      return newHistory.slice(-1000);
    });

    // Update daily usage
    const today = new Date().toDateString();
    const todayEntries = entry.timestamp >= new Date(today).getTime();
    
    if (todayEntries) {
      dailyUsage.update(usage => ({
        prompt: usage.prompt + entry.tokenUsage.prompt,
        completion: usage.completion + entry.tokenUsage.completion,
        total: usage.total + entry.tokenUsage.total,
        cost: (usage.cost || 0) + (entry.tokenUsage.cost || 0)
      }));
    }
  },

  /**
   * Reset daily usage (called at midnight)
   */
  resetDailyUsage(): void {
    dailyUsage.set({ prompt: 0, completion: 0, total: 0, cost: 0 });
  },

  /**
   * Get usage statistics for a period
   */
  getUsageStats(days: number = 7): {
    totalTokens: number;
    totalCost: number;
    averageResponseTime: number;
    successRate: number;
    providerBreakdown: Record<string, number>;
  } {
    const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
    let entries: UsageEntry[] = [];
    
    usageHistory.subscribe(history => {
      entries = history.filter(entry => entry.timestamp >= cutoff);
    })();

    const totalTokens = entries.reduce((sum, entry) => sum + entry.tokenUsage.total, 0);
    const totalCost = entries.reduce((sum, entry) => sum + (entry.tokenUsage.cost || 0), 0);
    const avgResponseTime = entries.length > 0 
      ? entries.reduce((sum, entry) => sum + entry.responseTime, 0) / entries.length
      : 0;
    const successRate = entries.length > 0
      ? entries.filter(entry => entry.success).length / entries.length
      : 0;

    const providerBreakdown = entries.reduce((breakdown, entry) => {
      breakdown[entry.provider] = (breakdown[entry.provider] || 0) + entry.tokenUsage.total;
      return breakdown;
    }, {} as Record<string, number>);

    return {
      totalTokens,
      totalCost,
      averageResponseTime: avgResponseTime,
      successRate,
      providerBreakdown
    };
  }
};

// Initialize from stored settings on startup
const initSettings = settingsStore.get();

if (initSettings.ai?.selectedModel) {
  selectedModel.set(initSettings.ai.selectedModel);
}
if (initSettings.ai?.provider) {
  selectedProvider.set(initSettings.ai.provider);
}

// Auto-initialize when settings change
aiSettings.subscribe(async (settings) => {
  const typedSettings = settings as AISettings;
  
  // Check if AI is enabled and has valid configuration
  const hasValidConfig = typedSettings.enabled && (
    (typedSettings.provider === 'openai' && typedSettings.openaiApiKey) ||
    (typedSettings.provider === 'anthropic' && typedSettings.anthropicApiKey) ||
    (typedSettings.provider === 'google' && typedSettings.googleApiKey) ||
    (typedSettings.provider === 'upstage' && typedSettings.upstageApiKey) ||
    (typedSettings.provider === 'openrouter') // OpenRouter doesn't need API key
  );
  
  if (hasValidConfig) {
    // Set the selected model and provider from settings
    if (typedSettings.selectedModel) {
      selectedModel.set(typedSettings.selectedModel);
    }
    if (typedSettings.provider) {
      selectedProvider.set(typedSettings.provider);
    }
    await aiServiceActions.initialize();
  } else {
    // Clear AI service if not properly configured
    if (aiServiceInstance) {
      aiServiceInstance = null;
      aiServiceState.update(state => ({
        ...state,
        isInitialized: false,
        currentModel: null,
        lastError: null
      }));
    }
  }
});

// Export current service instance for direct access if needed
export const getAIServiceInstance = () => aiServiceInstance;

// Helper functions
export function formatTokenCount(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  } else if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
}

export function formatCost(cost: number): string {
  if (cost === 0) return 'Free';
  if (cost < 0.001) return `<$0.001`;
  if (cost < 1) return `$${cost.toFixed(3)}`;
  return `$${cost.toFixed(2)}`;
}

export function calculateEstimatedCost(tokens: number, costPerToken: number): number {
  return tokens * costPerToken;
}

// Reset daily usage at midnight
if (typeof window !== 'undefined') {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const msUntilMidnight = tomorrow.getTime() - now.getTime();
  
  setTimeout(() => {
    aiServiceActions.resetDailyUsage();
    // Set up daily interval
    setInterval(() => {
      aiServiceActions.resetDailyUsage();
    }, 24 * 60 * 60 * 1000);
  }, msUntilMidnight);
}